import { useEffect, useMemo, useRef, useState } from 'react';
import { collectPageKeys, applySelectedBatches } from '../lib/bulkSelection.js';
import { useLongPress } from '../lib/useLongPress.js';
import { SelectionActionBar } from '../components/SelectionActionBar.js';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Bell,
  CalendarCheck,
  CheckCircle2,
  ChevronDown,
  Clock,
  FileText,
  Phone,
  Plus,
  Search,
  Send,
  UserPlus,
} from 'lucide-react';
import {
  REMINDER_STATUSES,
  LIMITS,
  type AuditEntry,
  type Inquiry,
  type Notification,
  type NotificationType,
  type Paginated,
  type Reminder,
  type ReminderStatus,
} from '@worki/shared';
import { api, buildQuery, RequestError } from '../lib/api.js';
import { useDebounced } from '../lib/useDebounced.js';
import { usePageSearch } from '../lib/pageSearch.js';
import { currentLocale } from '../i18n.js';
import { formatDateTime, formatRelative } from '../lib/format.js';
import { ActivityRow } from '../components/ActivityRow.js';
import { AnchoredMenu } from '../components/AnchoredMenu.js';
import { Select } from '../components/ui.js';
import { ConfirmDialog } from '../components/Modal.js';
import { DetailDialog } from '../components/DetailDialog.js';
import { ItemActions } from '../components/ItemActions.js';
import { ReminderDialog } from '../components/ReminderDialog.js';
import { useToast } from '../components/Toast.js';
import { cn } from '../lib/cn.js';

/**
 * Which kinds of row the feed is showing. Activity is the audit log, which is a
 * record of what already happened and not something that needs attention, so
 * "all" does not include it: it is here only when asked for by name.
 */
const FEED_FILTERS = ['all', 'notification', 'reminder', 'meeting', 'activity'] as const;
type FeedFilter = (typeof FEED_FILTERS)[number];

/** One icon per notification type, so a glance says what happened. */
const NOTIFICATION_ICON: Record<NotificationType, typeof Bell> = {
  quote_opened: FileText,
  quote_approved: CheckCircle2,
  quote_rejected: Send,
  reminder_due: Bell,
  inquiry_created: UserPlus,
};

interface FeedNotification extends Notification {
  vars: Record<string, string | number>;
}

/**
 * REM-002 — reminders and notifications as one feed.
 *
 * They were two separate places to look: a table of reminders here, a bell
 * popup for notifications. Both answer the same question — what needs my
 * attention — so they are one list, filterable to either.
 *
 * Meetings are the third kind, and they are not an entity of their own: an
 * appointment in Worki is a date on the enquiry it was booked for, so the row
 * opens that enquiry rather than a calendar entry that would have nothing else
 * on it.
 */
export function RemindersPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const locale = currentLocale();
  const toast = useToast();

  const [filter, setFilter] = useState<FeedFilter>('all');
  const [filterOpen, setFilterOpen] = useState(false);
  const filterRef = useRef<HTMLButtonElement>(null);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<ReminderStatus | ''>('open');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [editing, setEditing] = useState<Reminder | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<Reminder | null>(null);
  const [preview, setPreview] = useState<{ badge: string; title: string; meta: string; body?: string } | null>(null);

  const [page, setPage] = useState(1);
  const [selection, updateSelection] = useState<Set<string>>(new Set());
  const [selectMode, setSelectMode] = useState(false);
  const [confirmBulkDelete, setConfirmBulkDelete] = useState(false);
  const selectionEpoch = useRef(0);
  const selectionReadContext = useRef('');
  const setSelection = (next: Parameters<typeof updateSelection>[0]) => { selectionEpoch.current++; updateSelection(next); };
  useEffect(() => () => { selectionEpoch.current++; }, []);
  const toggleSelect = (id: string) => {
    if (!selection.has(id) && selection.size >= 10000) { toast.error(t('common.bulkSelectionLimit', { count: 10000 })); return; }
    setSelection(current => { const next = new Set(current); if (next.has(id)) next.delete(id); else next.add(id); return next; });
  };
  const longPress = useLongPress(id => { setSelection(new Set([id])); setSelectMode(true); });

  /**
   * `?new=1` opens the form, so the create sheet in the bottom bar can send the
   * user straight into it. An effect and not initial state: the sheet is often
   * opened while already standing on this screen, and that is a search change
   * with no remount.
   */
  const [params, setParams] = useSearchParams();
  useEffect(() => {
    if (params.get('new') !== '1') return;
    setEditing(null);
    setDialogOpen(true);
  }, [params]);

  const clearNewParam = () => {
    if (!params.get('new')) return;
    params.delete('new');
    setParams(params, { replace: true });
  };

  const debouncedSearch = useDebounced(search, 300);
  selectionReadContext.current = JSON.stringify([search, debouncedSearch, status, from, to, filter, page]);
  useEffect(() => { selectionEpoch.current++; updateSelection(new Set()); setSelectMode(false); setPage(1); }, [debouncedSearch, status, from, to, filter]);
  const reminderFilters = {
    q: debouncedSearch, status,
    from: from ? new Date(`${from}T00:00:00`).toISOString() : '',
    to: to ? new Date(`${to}T23:59:59.999`).toISOString() : '',
    sort: 'dueAt', dir: 'asc',
  };
  const selectAllMatching = useMutation({
    mutationFn: async (capture: { epoch: number; context: string; filters: typeof reminderFilters }) => collectPageKeys(async page => {
      if (capture.epoch !== selectionEpoch.current || capture.context !== selectionReadContext.current) throw new Error('Selection cancelled');
      const result = await api.get<Paginated<Reminder>>(`/reminders${buildQuery({ ...capture.filters, page, pageSize: 100 })}`);
      if (result.total > 10000) throw new Error('common.bulkSelectionLimit');
      return result;
    }, row => row.id),
    onSuccess: (ids, capture) => {
      if (capture.epoch !== selectionEpoch.current || capture.context !== selectionReadContext.current) return;
      setSelection(new Set(ids)); setSelectMode(ids.length > 0);
    },
    onError: (error, capture) => {
      if (capture.epoch === selectionEpoch.current && capture.context === selectionReadContext.current) toast.error(t(error.message === 'common.bulkSelectionLimit' ? error.message : 'errors.network', { count: 10000 }));
    },
  });
  /* UI-11 — the box that stood over the list is in the bar now, beside the
     name of the screen, which is where every screen's search is. Over the
     activity it searches the log and says so, as the full history screen does. */
  usePageSearch(search, setSearch, t(filter === 'activity' ? 'common.search' : 'reminders.searchPlaceholder'));

  /**
   * The two date inputs mean whole days in the user's own timezone, so the
   * bounds are built without a zone — `new Date('…T23:59:59')` reads as local —
   * and only then converted for a server that compares in UTC. The end bound
   * carries milliseconds because the last second of the day is part of it.
   */
  const reminders = useQuery({
    queryKey: ['reminders', { debouncedSearch, status, from, to, ...(page > 1 ? { page } : {}) }],
    queryFn: () =>
      api.get<Paginated<Reminder>>(
        `/reminders${buildQuery({ ...reminderFilters, page, pageSize: 50 })}`,
      ),
  });

  const notifications = useQuery({
    queryKey: ['notifications'],
    queryFn: () => api.get<{ items: FeedNotification[]; unread: number }>('/notifications'),
  });

  /* The same day bounds the reminders half uses, so the date filter narrows the
     whole feed instead of half of it. Ascending by appointment: this list is a
     diary, and the next thing in it is the one that matters. */
  const meetings = useQuery({
    queryKey: ['inquiries', 'meetings', { debouncedSearch, from, to }],
    queryFn: () =>
      api.get<Paginated<Inquiry>>(
        `/inquiries${buildQuery({
          hasMeeting: 'true',
          q: debouncedSearch,
          meetingFrom: from ? new Date(`${from}T00:00:00`).toISOString() : '',
          meetingTo: to ? new Date(`${to}T23:59:59.999`).toISOString() : '',
          sort: 'meetingAt',
          dir: 'asc',
          page: 1,
          pageSize: 50,
        })}`,
      ),
  });

  /* 1.15 — the log the full history screen reads, its first page, asked for
     only once "activity" is chosen: "all" never shows it, so the other kinds
     neither wait on it nor pay for it. Same key and page size as that screen,
     so going on to it from here finds the page already loaded. */
  const activity = useQuery({
    queryKey: ['activity', { debouncedSearch, page: 1 }],
    queryFn: () =>
      api.get<Paginated<AuditEntry>>(`/activity${buildQuery({ q: debouncedSearch, page: 1, pageSize: 30 })}`),
    enabled: filter === 'activity',
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['reminders'] });
    void queryClient.invalidateQueries({ queryKey: ['calendar'] });
    void queryClient.invalidateQueries({ queryKey: ['notifications'] });
    void queryClient.invalidateQueries({ queryKey: ['inquiries'] });
    void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    void queryClient.invalidateQueries({ queryKey: ['activity'] });
  };

  /**
   * Every mutation on this page used to swallow its failure: completing a
   * reminder someone else had already deleted, or snoozing one that is no longer
   * open, left the row exactly as it was with nothing said. The refresh matters
   * as much as the message — it pulls the row back in line with the server.
   */
  const fail = (error: unknown) => {
    const code = error instanceof RequestError ? (Object.values(error.fields)[0] ?? error.code) : 'errors.unknown';
    toast.error(t(code));
    invalidate();
  };

  const toggle = useMutation({
    mutationFn: (reminder: Reminder) =>
      api.post(`/reminders/${reminder.id}/${reminder.status === 'open' ? 'complete' : 'reopen'}`),
    onSuccess: invalidate,
    onError: fail,
  });

  const remove = useMutation({
    mutationFn: (reminder: Reminder) => api.post('/reminders/bulk/delete', { ids: [reminder.id] }),
    onSuccess: () => {
      setConfirmDelete(null);
      invalidate();
    },
    onError: (error) => {
      setConfirmDelete(null);
      fail(error);
    },
  });

  const bulkAction = useMutation({
    mutationFn: async (action: 'complete' | 'delete') => {
      selectionEpoch.current++;
      const attempted = [...selection];
      const result = await applySelectedBatches(attempted, async ids => {
        const answer = await api.post<{ updated?: number; deleted?: number; failed: string[] }>(`/reminders/bulk/${action}`, { ids });
        return { ok: answer.updated ?? answer.deleted ?? 0, failed: answer.failed };
      }, LIMITS.maxBulkInvoices);
      return { ...result, attempted };
    },
    onSuccess: (result) => {
      setConfirmBulkDelete(false);
      setSelection(current => new Set([...current].filter(id => !result.attempted.includes(id) || result.failed.includes(id))));
      invalidate();
      if (result.failed.length) toast.error(t('common.bulkSomeFailed', { count: result.failed.length }));
    },
  });

  /**
   * Snoozing is deliberately invisible in `dueAt` — REM-007 keeps the reporting
   * date fixed and only moves the next alert. That left the button with no
   * effect a user could see at all: nothing changed on the row, nothing was
   * said. The new alert time now appears under the due date, and the toast
   * repeats it, so "snooze a day" reports what it did.
   *
   * A day from *now* is also the wrong day for a reminder that is not due yet:
   * it would pull next week's alert forward to tomorrow. Push from whichever is
   * later, the current alert time or now.
   */
  const snooze = useMutation({
    mutationFn: (reminder: Reminder) => {
      const base = Math.max(Date.now(), new Date(reminder.snoozedUntil ?? reminder.dueAt).getTime());
      const until = new Date(base + 86_400_000).toISOString();
      return api.post<Reminder>(`/reminders/${reminder.id}/snooze`, { until });
    },
    onSuccess: (updated) => {
      invalidate();
      toast.success(t('reminders.snoozedUntil', { date: formatDateTime(updated.snoozedUntil ?? updated.dueAt, locale) }));
    },
    onError: fail,
  });

  const markRead = useMutation({
    mutationFn: (notification: Notification) => api.post(`/notifications/${notification.id}/read`),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['notifications'] }),
  });

  const markAllRead = useMutation({
    mutationFn: () => api.post('/notifications/read-all'),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['notifications'] }),
  });

  /**
   * `sourceLabel` is either a plain i18n key or `key:value` when the origin
   * carries a number (a quote). Splitting here keeps the API free of rendered
   * text while still letting the row say which quote it came from.
   */
  const renderSource = (reminder: Reminder) => {
    if (!reminder.sourceLabel) return t(`enum.reminderSource.${reminder.source}`);
    const [key, value] = reminder.sourceLabel.split(':');
    return t(key!, { number: value ?? '' });
  };

  /*
   * One list, newest first. Reminders sort by when they are due and
   * notifications by when they arrived; both are "the moment this row is
   * about", so they interleave on that one timestamp.
   */
  type Row =
    | { kind: 'notification'; key: string; at: number; notification: FeedNotification }
    | { kind: 'reminder'; key: string; at: number; reminder: Reminder }
    | { kind: 'meeting'; key: string; at: number; inquiry: Inquiry }
    | { kind: 'activity'; key: string; at: number; entry: AuditEntry };

  const rows = useMemo<Row[]>(() => {
    const notificationRows: Row[] = (notifications.data?.items ?? []).map((notification) => ({
      kind: 'notification',
      key: `n-${notification.id}`,
      at: new Date(notification.createdAt).getTime(),
      notification,
    }));
    const reminderRows: Row[] = (reminders.data?.items ?? []).map((reminder) => ({
      kind: 'reminder',
      key: `r-${reminder.id}`,
      at: new Date(reminder.dueAt).getTime(),
      reminder,
    }));
    const meetingRows: Row[] = (meetings.data?.items ?? [])
      .filter((inquiry) => inquiry.meetingAt)
      .map((inquiry) => ({
        kind: 'meeting',
        key: `m-${inquiry.id}`,
        at: new Date(inquiry.meetingAt!).getTime(),
        inquiry,
      }));
    const activityRows: Row[] = (activity.data?.items ?? []).map((entry) => ({
      kind: 'activity',
      key: `a-${entry.id}`,
      at: new Date(entry.createdAt).getTime(),
      entry,
    }));
    // A text search reaches reminders and enquiries on the server but not
    // notifications, so while one is typed that third of the feed would be
    // unfiltered noise beside two filtered ones.
    const all = debouncedSearch
      ? [...reminderRows, ...meetingRows]
      : [...notificationRows, ...reminderRows, ...meetingRows];
    // The log is not one of those kinds: "all" is what needs attention.
    return (filter === 'activity' ? activityRows : all)
      .filter((row) => filter === 'all' || row.kind === filter)
      .sort((a, b) => b.at - a.at);
  }, [notifications.data, reminders.data, meetings.data, activity.data, filter, debouncedSearch]);

  const pageReminderIds = rows.filter(row => row.kind === 'reminder').map(row => row.reminder.id);
  const unread = notifications.data?.unread ?? 0;
  const loading = reminders.isLoading || notifications.isLoading || meetings.isLoading || activity.isLoading;

  return (
    <div className="max-w-3xl mx-auto animate-fade-up">
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <div className="relative">
          <button
            type="button"
            ref={filterRef}
            onClick={() => setFilterOpen((open) => !open)}
            className="h-9 px-3.5 rounded-xl bg-card border border-border hover:border-primary/40 transition-colors flex items-center gap-2 shadow-sm"
          >
            <span className="text-sm font-medium">{t(`reminders.feed.${filter}`)}</span>
            <ChevronDown
              className={cn('h-4 w-4 text-muted-foreground transition-transform', filterOpen && 'rotate-180')}
            />
          </button>
          {/* Portalled: this page is one `animate-fade-up` block, whose held
              transform makes it a stacking context, and it scrolls inside
              `page-container`. Either one alone would swallow the menu. */}
          <AnchoredMenu
            open={filterOpen}
            onClose={() => setFilterOpen(false)}
            anchorRef={filterRef}
            width={256}
          >
            {FEED_FILTERS.map((option) => (
              <button
                key={option}
                type="button"
                onClick={() => {
                  setFilter(option);
                  setFilterOpen(false);
                }}
                className={cn(
                  'w-full text-start px-4 py-2.5 text-sm hover:bg-muted/60 transition-colors',
                  filter === option && 'text-primary font-semibold',
                )}
              >
                {t(`reminders.feed.${option}`)}
              </button>
            ))}
            {/* The reminder-only filters live in the same panel rather than a
                filter bar of their own: they narrow the same list. */}
            <div className="border-t border-border p-3 space-y-2">
              <Select
                value={status}
                aria-label={t('reminders.status')}
                onChange={(value) => setStatus(value as ReminderStatus | '')}
                className="h-9 px-2 text-xs bg-muted"
              >
                <option value="">{t('common.allOf', { label: t('reminders.status') })}</option>
                {REMINDER_STATUSES.map((option) => (
                  <option key={option} value={option}>
                    {t(`enum.reminderStatus.${option}`)}
                  </option>
                ))}
              </Select>
              <div className="flex items-center gap-2">
                <input
                  type="date"
                  value={from}
                  aria-label={t('reminders.fromDate')}
                  onChange={(event) => setFrom(event.target.value)}
                  className="flex-1 min-w-0 h-9 px-2 rounded-lg bg-muted text-xs outline-none"
                />
                <input
                  type="date"
                  value={to}
                  aria-label={t('reminders.toDate')}
                  onChange={(event) => setTo(event.target.value)}
                  className="flex-1 min-w-0 h-9 px-2 rounded-lg bg-muted text-xs outline-none"
                />
              </div>
            </div>
          </AnchoredMenu>
        </div>

        <div className="flex items-center gap-2">
          {unread > 0 && (
            <button
              type="button"
              onClick={() => markAllRead.mutate()}
              className="text-xs text-primary hover:underline"
            >
              {t('notifications.markAllRead')}
            </button>
          )}
          <button
            type="button"
            onClick={() => {
              setEditing(null);
              setDialogOpen(true);
            }}
            className="h-9 px-4 rounded-xl bg-primary text-primary-foreground text-sm font-medium flex items-center gap-2 hover:opacity-90 transition-all active:scale-[0.97] shadow-sm"
          >
            <Plus className="h-4 w-4" />
            {t('reminders.new')}
          </button>
        </div>
      </div>

      {(filter === 'all' || filter === 'reminder') && <button type="button" aria-pressed={selectMode} onClick={() => { setSelection(new Set()); setSelectMode(!selectMode); }} className="mb-3 text-sm text-primary">{t('common.selectMode')}</button>}
      {selection.size > 0 && <SelectionActionBar count={selection.size} onClear={() => { setSelection(new Set()); setSelectMode(false); }}>
        <button type="button" disabled={bulkAction.isPending || reminders.isFetching} onClick={() => setSelection(new Set(pageReminderIds))} className="text-xs text-primary">{t('invoices.selectPageOnly', { count: pageReminderIds.length })}</button>
        <button type="button" disabled={selectAllMatching.isPending || bulkAction.isPending || reminders.isFetching || search !== debouncedSearch} onClick={() => selectAllMatching.mutate({ epoch: ++selectionEpoch.current, context: selectionReadContext.current, filters: { ...reminderFilters } })} className="text-xs text-primary">{t('reminders.selectAllMatching', { count: reminders.data?.total ?? 0 })}</button>
        <button type="button" disabled={bulkAction.isPending} onClick={() => bulkAction.mutate('complete')} className="text-xs text-primary">{t('reminders.bulkComplete')}</button>
        <button type="button" disabled={bulkAction.isPending} onClick={() => setConfirmBulkDelete(true)} className="text-xs text-destructive">{t('common.delete')}</button>
      </SelectionActionBar>}

      <div className="space-y-2">
        {rows.map((row) => {
          if (row.kind === 'notification') {
            const notification = row.notification;
            const NotificationIcon = NOTIFICATION_ICON[notification.type] ?? Bell;
            const unreadRow = !notification.readAt;
            const preview_ = () =>
              setPreview({
                badge: t('reminders.feed.notification'),
                title: t(notification.title, notification.vars),
                meta: formatRelative(notification.createdAt, locale),
              });
            // Opening a notification means going to what it is about; when it is
            // about nothing navigable, the text itself is all there is to show.
            const open = () => {
              if (unreadRow) markRead.mutate(notification);
              if (notification.link) navigate(notification.link);
              else preview_();
            };
            return (
              <div
                key={row.key}
                role="button"
                tabIndex={0}
                onClick={open}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    open();
                  }
                }}
                className={cn(
                  'list-tint rounded-xl border bg-card px-4 py-3 flex items-center gap-3 cursor-pointer transition-colors',
                  unreadRow ? 'border-primary/25 bg-primary/5' : 'border-border/60 hover:border-primary/15',
                )}
              >
                <div className={cn('p-1.5 rounded-md shrink-0', unreadRow ? 'bg-primary/10' : 'bg-muted')}>
                  <NotificationIcon className={cn('h-4 w-4', unreadRow ? 'text-primary' : 'text-muted-foreground')} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className={cn('text-sm truncate', unreadRow && 'font-medium')}>
                    {t(notification.title, notification.vars)}
                  </p>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-[11px] text-muted-foreground">
                      {formatRelative(notification.createdAt, locale)}
                    </span>
                    <span className="text-[10px] px-2 py-0.5 rounded-full font-medium bg-muted text-muted-foreground">
                      {t('reminders.feed.notification')}
                    </span>
                  </div>
                </div>
                {unreadRow && <span className="h-2 w-2 rounded-full bg-primary shrink-0" />}
                <div onClick={(event) => event.stopPropagation()}>
                  {/* A notification is a record of something that happened: there
                      is nothing to edit, and the API has no delete for it — so
                      the menu only reads it without leaving the feed. */}
                  <ItemActions onView={preview_} />
                </div>
              </div>
            );
          }

          if (row.kind === 'meeting') {
            const inquiry = row.inquiry;
            const name = inquiry.customer?.name ?? '—';
            const past = row.at < Date.now();
            const preview_ = () =>
              setPreview({
                badge: t('reminders.feed.meeting'),
                title: name,
                meta: `${formatDateTime(inquiry.meetingAt, locale)} · ${t(`enum.inquirySource.${inquiry.source}`)}`,
                body: inquiry.subject ?? inquiry.message ?? undefined,
              });
            // The appointment lives on the enquiry, so that is what opens —
            // with the detail sheet already up, which is where the date is
            // changed or cleared.
            const open = () => navigate(`/inquiries?open=${inquiry.id}`);
            return (
              <div
                key={row.key}
                role="button"
                tabIndex={0}
                onClick={open}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    open();
                  }
                }}
                className={cn(
                  'list-tint rounded-xl border bg-card px-4 py-3 flex items-center gap-3 cursor-pointer transition-colors',
                  past ? 'border-border/60 hover:border-primary/15' : 'border-info/25 bg-info/5',
                )}
              >
                <div className={cn('p-1.5 rounded-md shrink-0', past ? 'bg-muted' : 'bg-info/10')}>
                  <CalendarCheck className={cn('h-4 w-4', past ? 'text-muted-foreground' : 'text-info')} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className={cn('text-sm truncate', !past && 'font-medium')}>{name}</p>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-[11px] text-muted-foreground ltr-nums">
                      {formatDateTime(inquiry.meetingAt, locale)}
                    </span>
                    <span className="text-[10px] px-2 py-0.5 rounded-full font-medium bg-muted text-muted-foreground">
                      {t('reminders.feed.meeting')}
                    </span>
                  </div>
                </div>
                <div onClick={(event) => event.stopPropagation()}>
                  {/* Editing and clearing both belong on the enquiry, where the
                      status that justifies the appointment is edited too. */}
                  <ItemActions onView={preview_} />
                </div>
              </div>
            );
          }

          if (row.kind === 'activity') return <ActivityRow key={row.key} entry={row.entry} />;

          const reminder = row.reminder;
          const done = reminder.status === 'completed';
          const overdue = !done && new Date(reminder.dueAt).getTime() < Date.now();
          const open = () =>
            setPreview({
              badge: t('reminders.feed.reminder'),
              title: reminder.title,
              meta: `${formatDateTime(reminder.dueAt, locale)} · ${renderSource(reminder)}`,
              body: reminder.notes ?? (done ? t('reminders.previewDone') : t('reminders.previewOpen')),
            });
          return (
            <div
              key={row.key}
              {...longPress.handlers(reminder.id)}
              role="button"
              tabIndex={0}
              onClick={() => { if (longPress.consumeClick()) return; if (selectMode) toggleSelect(reminder.id); else open(); }}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  if (selectMode) toggleSelect(reminder.id); else open();
                }
              }}
              className={cn(
                'list-tint rounded-xl border bg-card px-4 py-3 transition-all cursor-pointer',
                done ? 'opacity-50 border-border/60' : 'hover:shadow-sm border-border/60 hover:border-primary/15',
                overdue && 'border-destructive/30',
              )}
            >
              <div className="flex items-center gap-3">
                {selectMode && <input type="checkbox" checked={selection.has(reminder.id)} aria-label={t('common.selectRow')} onClick={event => event.stopPropagation()} onKeyDown={event => event.stopPropagation()} onChange={() => toggleSelect(reminder.id)} className="h-5 w-5 shrink-0" />}
                <button
                  type="button"
                  aria-label={done ? t('reminders.markOpen') : t('reminders.markComplete')}
                  onClick={(event) => {
                    event.stopPropagation();
                    toggle.mutate(reminder);
                  }}
                  className={cn(
                    'h-5 w-5 rounded-full border-2 shrink-0 transition-all flex items-center justify-center',
                    done
                      ? 'bg-success border-success'
                      : 'border-muted-foreground/30 hover:border-success hover:bg-success/10',
                  )}
                >
                  {done && <CheckCircle2 className="h-3.5 w-3.5 text-success-foreground" />}
                </button>
                <div className={cn('p-1.5 rounded-md shrink-0', overdue ? 'bg-destructive/10' : 'bg-info/10')}>
                  <Clock className={cn('h-4 w-4', overdue ? 'text-destructive' : 'text-info')} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className={cn('text-sm font-medium truncate', done && 'line-through text-muted-foreground')}>
                    {reminder.title}
                  </p>
                  <div className="flex items-center gap-2 mt-1 flex-wrap">
                    <span className="text-[11px] text-muted-foreground ltr-nums">
                      {formatDateTime(reminder.dueAt, locale)}
                    </span>
                    <span className="text-[10px] px-2 py-0.5 rounded-full font-medium bg-info/10 text-info">
                      {t('reminders.feed.reminder')}
                    </span>
                    {overdue && (
                      <span className="text-[10px] px-2 py-0.5 rounded-full font-medium bg-destructive/10 text-destructive">
                        {t('reminders.overdue')}
                      </span>
                    )}
                    {/* The due date does not move when a reminder is snoozed, so
                        the row says when it will speak up next. */}
                    {!done && reminder.snoozedUntil && (
                      <span className="text-[11px] text-muted-foreground ltr-nums">
                        {t('reminders.snoozedUntil', { date: formatDateTime(reminder.snoozedUntil, locale) })}
                      </span>
                    )}
                  </div>
                </div>
                <div className="shrink-0" onClick={(event) => event.stopPropagation()}>
                  <ItemActions
                    onView={open}
                    onEdit={() => {
                      setEditing(reminder);
                      setDialogOpen(true);
                    }}
                    {...(done ? {} : { onSnooze: () => snooze.mutate(reminder) })}
                    onDelete={() => setConfirmDelete(reminder)}
                  />
                </div>
              </div>
            </div>
          );
        })}

        {/* The dashboard's link to the full history came here with its block:
            the log is paged there, and nothing else leads to it. */}
        {filter === 'activity' && rows.length > 0 && (
          <button
            type="button"
            onClick={() => navigate('/activity')}
            className="w-full text-center py-2 text-sm text-primary hover:underline"
          >
            {t('activity.viewAll')}
          </button>
        )}

        {rows.length === 0 && (
          <div className="text-center py-12 text-muted-foreground">
            <Bell className="h-10 w-10 mx-auto mb-3 text-muted-foreground/30" />
            <p className="text-sm">
              {loading ? t('common.loading') : t(filter === 'activity' ? 'activity.empty' : 'reminders.emptyTitle')}
            </p>
            {/* "Create a reminder" is no answer to an empty log. */}
            {!loading && filter !== 'activity' && <p className="text-xs mt-1">{t('reminders.emptyBody')}</p>}
          </div>
        )}
      </div>

      {filter === 'reminder' && reminders.data && reminders.data.total > 50 && <nav aria-label={t('common.page')} className="flex items-center justify-center gap-3 my-4">
        <button type="button" disabled={page <= 1 || reminders.isFetching} onClick={() => setPage(at => at - 1)}>{t('common.previous')}</button>
        <span>{page} / {Math.ceil(reminders.data.total / 50)}</span>
        <button type="button" disabled={page * 50 >= reminders.data.total || reminders.isFetching} onClick={() => setPage(at => at + 1)}>{t('common.next')}</button>
      </nav>}
      <ConfirmDialog open={confirmBulkDelete} title={t('common.delete')} message={t('reminders.bulkDeleteConfirm', { count: selection.size })} loading={bulkAction.isPending} onCancel={() => setConfirmBulkDelete(false)} onConfirm={() => bulkAction.mutate('delete')} />

      <ReminderDialog
        open={dialogOpen}
        reminder={editing}
        onClose={() => {
          setDialogOpen(false);
          setEditing(null);
          clearNewParam();
        }}
      />

      <DetailDialog
        open={Boolean(preview)}
        onClose={() => setPreview(null)}
        title={preview?.badge ?? ''}
      >
        <div className="space-y-2">
          <h3 className="text-base font-bold leading-relaxed">{preview?.title}</h3>
          <p className="text-xs text-muted-foreground ltr-nums">{preview?.meta}</p>
          {preview?.body && <p className="text-sm bg-muted/60 rounded-xl p-3">{preview.body}</p>}
        </div>
      </DetailDialog>

      <ConfirmDialog
        open={Boolean(confirmDelete)}
        title={t('common.delete')}
        message={t('reminders.deleteConfirm')}
        loading={remove.isPending}
        onCancel={() => setConfirmDelete(null)}
        onConfirm={() => confirmDelete && remove.mutate(confirmDelete)}
      />
    </div>
  );
}
